import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import useUserCars from "../../User/hooks/useUserCars";
import axios from "axios";
import config from "../../configuration/config";
import { useTranslation } from "react-i18next";
import { callFuncData } from "../../services/funcService";
// ============================================
// إعدادات التصميم القابلة للتخصيص
// ============================================
const themeConfig = {
  // أحجام النصوص حسب المستويات
  typography: {
    // العناوين
    headings: {
      h1: "text-3xl font-bold",
      h2: "text-3xl font-bold",
      h3: "text-3xl font-bold",
      h4: "text-2xl font-bold",
      h5: "text-2xl font-bold",
    },

    // العناوين الفرعية
    subheadings: {
      sub1: "text-3xl font-semibold",
      sub2: "text-2xl font-semibold",
      sub3: "text-2xl font-semibold",
    },

    // النصوص الرئيسية
    body: {
      large: "text-3xl",
      medium: "text-2xl",
      small: "text-2xl",
      xsmall: "text-xl",
    },

    // النصوص التوضيحية
    captions: {
      caption1: "text-2xl text-gray-500",
      caption2: "text-xl text-gray-500",
    },

    // الحقول والإدخال
    inputs: {
      label: "text-3xl font-medium",
      placeholder: "text-3xl",
      helper: "text-3xl text-gray-500",
      error: "text-3xl text-red-500",
    },

    // الأزرار
    buttons: {
      large: "text-3xl font-medium",
      medium: "text-2xl font-medium",
      small: "text-2xl font-medium",
      xsmall: "text-2xl font-medium",
    },

    // الروابط
    links: {
      primary: "text-2xl text-green-600 hover:text-green-800",
      secondary: "text-2xl text-gray-600 hover:text-gray-800",
    },

    // الأيقونات
    icons: {
      xlarge: "text-3xl",
      large: "text-3xl",
      medium: "text-3xl",
      small: "text-2xl",
      xsmall: "text-2xl",
    },
  },

  // الأحجام العامة
  sizes: {
    // أحجام padding
    padding: {
      xs: "px-2 py-1",
      sm: "px-3 py-1.5",
      base: "px-4 py-2",
      md: "px-5 py-2.5",
      lg: "px-6 py-3",
      xl: "px-6 py-4",
      "2xl": "px-8 py-4",
    },

    // أحجام margin
    margin: {
      xs: "m-1",
      sm: "m-2",
      base: "m-3",
      md: "m-4",
      lg: "m-6",
      xl: "m-8",
    },

    // أحجام العناصر
    element: {
      small: "h-8 w-8",
      medium: "h-10 w-10",
      large: "h-12 w-12",
      xlarge: "h-14 w-14",
    },

    // أحجام الزوايا
    borderRadius: {
      sm: "rounded",
      base: "rounded-lg",
      md: "rounded-xl",
      lg: "rounded-2xl",
      full: "rounded-full",
    },
  },

  // فئات Tailwind للألوان (للاستخدام في className)
  colorClasses: {
    // فئات الخلفية
    bg: {
      primary: {
        50: "bg-primary",
        100: "bg-primary",
        200: "bg-primary",
        300: "bg-primary",
        400: "bg-primary",
        500: "bg-primary",
        600: "bg-primary",
        700: "bg-primary",
        800: "bg-primary",
        900: "bg-primary",
      },
      secondary: {
        50: "bg-red-50",
        100: "bg-red-100",
        200: "bg-red-200",
        300: "bg-red-300",
        400: "bg-red-400",
        500: "bg-red-500",
        600: "bg-red-600",
        700: "bg-red-700",
        800: "bg-red-800",
        900: "bg-red-900",
      },
      success: {
        50: "bg-green-50",
        100: "bg-green-100",
        200: "bg-green-200",
        300: "bg-green-300",
        400: "bg-green-400",
        500: "bg-green-500",
        600: "bg-green-600",
        700: "bg-green-700",
        800: "bg-green-800",
        900: "bg-green-900",
      },
      warning: {
        50: "bg-yellow-50",
        100: "bg-yellow-100",
        200: "bg-yellow-200",
        300: "bg-yellow-300",
        400: "bg-yellow-400",
        500: "bg-yellow-500",
        600: "bg-yellow-600",
        700: "bg-yellow-700",
        800: "bg-yellow-800",
        900: "bg-yellow-900",
      },
      error: {
        50: "bg-red-50",
        100: "bg-red-100",
        200: "bg-red-200",
        300: "bg-red-300",
        400: "bg-red-400",
        500: "bg-red-500",
        600: "bg-red-600",
        700: "bg-red-700",
        800: "bg-red-800",
        900: "bg-red-900",
      },
      neutral: {
        50: "bg-gray-50",
        100: "bg-gray-100",
        200: "bg-gray-200",
        300: "bg-gray-300",
        400: "bg-gray-400",
        500: "bg-gray-500",
        600: "bg-gray-600",
        700: "bg-gray-700",
        800: "bg-gray-800",
        900: "bg-gray-900",
      },
      // ألوان إضافية
      white: "bg-white",
      black: "bg-black",
      transparent: "bg-transparent",
    },

    // فئات النصوص
    text: {
      primary: {
        50: "text-green-50",
        100: "text-green-100",
        200: "text-green-200",
        300: "text-green-300",
        400: "text-green-400",
        500: "text-green-500",
        600: "text-green-600",
        700: "text-green-700",
        800: "text-green-800",
        900: "text-green-900",
      },
      secondary: {
        50: "text-red-50",
        100: "text-red-100",
        200: "text-red-200",
        300: "text-red-300",
        400: "text-red-400",
        500: "text-red-500",
        600: "text-red-600",
        700: "text-red-700",
        800: "text-red-800",
        900: "text-red-900",
      },
      success: {
        50: "text-green-50",
        100: "text-green-100",
        200: "text-green-200",
        300: "text-green-300",
        400: "text-green-400",
        500: "text-green-500",
        600: "text-green-600",
        700: "text-green-700",
        800: "text-green-800",
        900: "text-green-900",
      },
      warning: {
        50: "text-yellow-50",
        100: "text-yellow-100",
        200: "text-yellow-200",
        300: "text-yellow-300",
        400: "text-yellow-400",
        500: "text-yellow-500",
        600: "text-yellow-600",
        700: "text-yellow-700",
        800: "text-yellow-800",
        900: "text-yellow-900",
      },
      error: {
        50: "text-red-50",
        100: "text-red-100",
        200: "text-red-200",
        300: "text-red-300",
        400: "text-red-400",
        500: "text-red-500",
        600: "text-red-600",
        700: "text-red-700",
        800: "text-red-800",
        900: "text-red-900",
      },
      neutral: {
        50: "text-gray-50",
        100: "text-gray-100",
        200: "text-gray-200",
        300: "text-gray-300",
        400: "text-gray-400",
        500: "text-gray-500",
        600: "text-gray-600",
        700: "text-gray-700",
        800: "text-gray-800",
        900: "text-gray-900",
      },
      // ألوان إضافية
      white: "text-white",
      black: "text-black",
      current: "text-current",
    },

    // فئات الحدود
    border: {
      primary: {
        50: "border-green-50",
        100: "border-green-100",
        200: "border-green-200",
        300: "border-green-300",
        400: "border-green-400",
        500: "border-green-500",
        600: "border-green-600",
        700: "border-green-700",
        800: "border-green-800",
        900: "border-green-900",
      },
      secondary: {
        50: "border-red-50",
        100: "border-red-100",
        200: "border-red-200",
        300: "border-red-300",
        400: "border-red-400",
        500: "border-red-500",
        600: "border-red-600",
        700: "border-red-700",
        800: "border-red-800",
        900: "border-red-900",
      },
      success: {
        50: "border-green-50",
        100: "border-green-100",
        200: "border-green-200",
        300: "border-green-300",
        400: "border-green-400",
        500: "border-green-500",
        600: "border-green-600",
        700: "border-green-700",
        800: "border-green-800",
        900: "border-green-900",
      },
      warning: {
        50: "border-yellow-50",
        100: "border-yellow-100",
        200: "border-yellow-200",
        300: "border-yellow-300",
        400: "border-yellow-400",
        500: "border-yellow-500",
        600: "border-yellow-600",
        700: "border-yellow-700",
        800: "border-yellow-800",
        900: "border-yellow-900",
      },
      error: {
        50: "border-red-50",
        100: "border-red-100",
        200: "border-red-200",
        300: "border-red-300",
        400: "border-red-400",
        500: "border-red-500",
        600: "border-red-600",
        700: "border-red-700",
        800: "border-red-800",
        900: "border-red-900",
      },
      neutral: {
        50: "border-gray-50",
        100: "border-gray-100",
        200: "border-gray-200",
        300: "border-gray-300",
        400: "border-gray-400",
        500: "border-gray-500",
        600: "border-gray-600",
        700: "border-gray-700",
        800: "border-gray-800",
        900: "border-gray-900",
      },
      // ألوان إضافية
      white: "border-white",
      black: "border-black",
      transparent: "border-transparent",
      current: "border-current",
    },
  },

  // الظلال
  shadows: {
    none: "shadow-none",
    sm: "shadow-sm",
    base: "shadow",
    md: "shadow-md",
    lg: "shadow-lg",
    xl: "shadow-xl",
    "2xl": "shadow-2xl",
    inner: "shadow-inner",
  },

  // الأنيميشن والتنتقالات
  transitions: {
    none: "transition-none",
    all: "transition-all",
    colors: "transition-colors",
    transform: "transition-transform",
    opacity: "transition-opacity",
    duration: {
      75: "duration-75",
      100: "duration-100",
      150: "duration-150",
      200: "duration-200",
      300: "duration-300",
      500: "duration-500",
      700: "duration-700",
      1000: "duration-1000",
    },
  },

  // الثوابت حسب العنصر
  components: {
    header: {
      bgColor: "bg-white",
      textColor: "text-gray-900",
      borderColor: "border-gray-200",
      shadow: "shadow-lg",
      padding: "py-6",
    },

    searchInput: {
      bgColor: "bg-white",
      borderColor: "border-gray-300",
      focusRingColor: "ring-green-500",
      focusBorderColor: "border-green-500",
      padding: "px-6 py-4",
      borderRadius: "rounded-xl",
    },

    buttons: {
      primary: {
        bgColor: "bg-green-600",
        hoverBgColor: "hover:bg-green-700",
        textColor: "text-white",
        padding: "px-6 py-4",
        borderRadius: "rounded-xl",
      },
      secondary: {
        bgColor: "bg-gray-800",
        hoverBgColor: "hover:bg-gray-900",
        textColor: "text-white",
        padding: "px-6 py-4",
        borderRadius: "rounded-xl",
      },
    },

    popup: {
      bgColor: "bg-white",
      borderRadius: "rounded-2xl",
      shadow: "shadow-2xl",
      overlayBg: "rgba(255, 255, 255, 0.1)",
      overlayBlur: "blur(1px)",
    },

    card: {
      bgColor: "bg-white",
      borderColor: "border-gray-200",
      borderRadius: "rounded-xl",
      shadow: "shadow-md",
      hoverShadow: "hover:shadow-lg",
    },
  },
};

const carSvg = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="20px" height="20px" fill="none">
      {" "}
      <path d="M24,13H4l2-4.6C6.6,7,8,6,9.6,6h8.7c1.6,0,3,1,3.7,2.4L24,13z" stroke="#4ecf6f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8,23H2v2c0,1.1,0.9,2,2,2h2c1.1,0,2-0.9,2-2V23z" stroke="#4ecf6f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="1" y1="12" x2="4.4" y2="12" stroke="#4ecf6f" strokeWidth="2" strokeLinecap="round" />
      <line x1="24" y1="12" x2="27" y2="12" stroke="#4ecf6f" strokeWidth="2" strokeLinecap="round" />
      <line x1="4" y1="18" x2="7" y2="18" stroke="#4ecf6f" strokeWidth="2" strokeLinecap="round" />
      <circle cx="24" cy="23" r="7" stroke="#4ecf6f" strokeWidth="2" />
      <line x1="24" y1="20" x2="24" y2="26" stroke="#4ecf6f" strokeWidth="2" strokeLinecap="round" />
      <line x1="21" y1="23" x2="27" y2="23" stroke="#4ecf6f" strokeWidth="2" strokeLinecap="round" />
      <path d="M17.6,20.1c-0.3-0.7-1-1.1-1.8-1.1h-3.5c-0.8,0-1.5,0.4-1.8,1.1L9,23" stroke="#4ecf6f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17,23H3c-1.1,0-2-0.9-2-2v-4c0-2.2,1.8-4,4-4h18c2.1,0,3.8,1.6,4,3.7" stroke="#4ecf6f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};
// ============================================
//  {/* عرض السيارة المختارة */}
// ============================================
export const PartsSearchSelectedCar = ({ selectedCarData, onOpenModelFilter, onSelectedCar }) => {
  const [selectedCar, setSelectedCar] = useState(selectedCarData);
  // جلب السيارات المحفوظة من localStorage
  useEffect(() => {
    setSelectedCar(selectedCarData);
  }, [selectedCarData]);

  return selectedCar ? (
    <div
      className={`mb-8 ${themeConfig.components.card.bgColor} ${themeConfig.sizes.borderRadius.md} ${themeConfig.components.card.shadow} p-6 border ${themeConfig.colorClasses.border.primary[200]}`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between">
        <div className="flex items-center mb-4 md:mb-0">
          <div className={`${themeConfig.colorClasses.bg.neutral[100]} p-3 ${themeConfig.sizes.borderRadius.base} ml-4`}>
            <span className={`${themeConfig.colorClasses.text.primary[600]} ${themeConfig.typography.icons.medium}`}>{carSvg()}</span>
          </div>
          <div>
            <h3 className={`font-bold ${themeConfig.colorClasses.text.neutral[800]} ${themeConfig.typography.subheadings.sub3}`}>أنت تبحث عن قطع متوافقة مع:</h3>
            <p className={`${themeConfig.colorClasses.text.primary[700]} ${themeConfig.typography.body.large}`}>{selectedCar.name}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => onOpenModelFilter(true)}
            className={`${themeConfig.colorClasses.text.primary[600]} hover:${themeConfig.colorClasses.text.primary[800]} hover:${themeConfig.colorClasses.bg.primary[50]} px-4 py-2 ${themeConfig.sizes.borderRadius.base} ${themeConfig.typography.buttons.small}`}>
            تغيير السيارة
          </button>
          <button
            onClick={() => onSelectedCar(null)}
            className={`${themeConfig.colorClasses.text.neutral[600]} hover:${themeConfig.colorClasses.text.neutral[800]} hover:${themeConfig.colorClasses.bg.neutral[100]} px-4 py-2 ${themeConfig.sizes.borderRadius.base} ${themeConfig.typography.buttons.small}`}>
            إلغاء الاختيار
          </button>
        </div>
      </div>
    </div>
  ) : null;
};
// ============================================
// المكون الرئيسي لرأس البحث والتخصيص حسب الموديل
// ============================================
const PartsSearchHeader = ({ onSearch, onOpenModelFilter }) => {
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(searchQuery);
    }
  };

  return (
    <div className={`${themeConfig.components.header.bgColor} ${themeConfig.components.header.shadow} border-b ${themeConfig.components.header.borderColor}`}>
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between">
          {/* الشعار والعنوان */}
          <div className="flex items-center mb-6 md:mb-0">
            <div className={`${themeConfig.colorClasses.bg.secondary[500]} p-3 ${themeConfig.sizes.borderRadius.base} ml-4`}>
              <span className={`text-white font-bold ${themeConfig.typography.icons.large}`}>ت</span>
            </div>
            <div>
              <h1 className={`${themeConfig.typography.headings.h2} ${themeConfig.components.header.textColor}`}>أجزاء تويوتا الرسمية</h1>
              <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.medium}`}>الموقع الرسمي لقطع غيار تويوتا في السعودية</p>
            </div>
          </div>

          {/* شريط البحث وأزرار التصفية */}
          <div className="w-full md:w-2/3 lg:w-1/2">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="ابحث برقم القطعة أو اسم القطعة..."
                  className={`w-full ${themeConfig.components.searchInput.padding} pr-12 border ${themeConfig.components.searchInput.borderColor} ${themeConfig.components.searchInput.borderRadius} focus:ring-2 ${themeConfig.components.searchInput.focusRingColor} focus:${themeConfig.components.searchInput.focusBorderColor} ${themeConfig.transitions.all} text-right ${themeConfig.typography.inputs.placeholder}`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  {" "}
                  <img width="10" height="10" className="inline filter-primary size-21" src="/assets/icons/search.svg" alt="search" />
                </div>
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className={`${themeConfig.colorClasses.text.neutral[400]} hover:${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.icons.small}`}>
                    ✕
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className={`${themeConfig.components.buttons.primary.bgColor} ${themeConfig.components.buttons.primary.hoverBgColor} ${themeConfig.components.buttons.primary.textColor} ${themeConfig.components.buttons.primary.padding} ${themeConfig.components.buttons.primary.borderRadius} ${themeConfig.typography.buttons.medium} ${themeConfig.transitions.all} whitespace-nowrap`}>
                  بحث
                </button>

                <button
                  type="button"
                  onClick={onOpenModelFilter}
                  className={`${themeConfig.components.buttons.secondary.bgColor} ${themeConfig.components.buttons.secondary.hoverBgColor} ${themeConfig.components.buttons.secondary.textColor} ${themeConfig.components.buttons.secondary.padding} ${themeConfig.components.buttons.secondary.borderRadius} ${themeConfig.typography.buttons.medium} ${themeConfig.transitions.all} flex items-center justify-center whitespace-nowrap`}>
                  <span className="ml-2 text-white">{carSvg()}</span>
                  تصنيف حسب موديل السيارة
                </button>
              </div>
            </form>

            {/* أمثلة سريعة للبحث */}
            <div className="mt-3 flex flex-wrap gap-2">
              <span className={`${themeConfig.typography.body.medium} ${themeConfig.colorClasses.text.neutral[500]}`}>أمثلة:</span>
              <button
                type="button"
                onClick={() => setSearchQuery("فلتر زيت")}
                className={`${themeConfig.typography.body.small} ${themeConfig.colorClasses.text.primary[600]} hover:${themeConfig.colorClasses.text.primary[800]} ${themeConfig.colorClasses.bg.primary[50]} hover:${themeConfig.colorClasses.bg.primary[100]} px-3 py-1 ${themeConfig.sizes.borderRadius.full} ${themeConfig.transitions.all}`}>
                فلتر زيت
              </button>
              <button
                type="button"
                onClick={() => setSearchQuery("90915-YZZE1")}
                className={`${themeConfig.typography.body.small} ${themeConfig.colorClasses.text.primary[600]} hover:${themeConfig.colorClasses.text.primary[800]} ${themeConfig.colorClasses.bg.primary[50]} hover:${themeConfig.colorClasses.bg.primary[100]} px-3 py-1 ${themeConfig.sizes.borderRadius.full} ${themeConfig.transitions.all}`}>
                90915-YZZE1
              </button>
              <button
                type="button"
                onClick={() => setSearchQuery("مكابح")}
                className={`${themeConfig.typography.body.small} ${themeConfig.colorClasses.text.primary[600]} hover:${themeConfig.colorClasses.text.primary[800]} ${themeConfig.colorClasses.bg.primary[50]} hover:${themeConfig.colorClasses.bg.primary[100]} px-3 py-1 ${themeConfig.sizes.borderRadius.full} ${themeConfig.transitions.all}`}>
                مكابح
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// المكون المنبثق لتصفية حسب موديل السيارة
// ============================================
export const ModelFilterPopup = ({ isOpen, onClose, onSelectModel, onAddNewCar, selectedCarData }) => {
  const [savedCars, setSavedCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCar, setSelectedCar] = useState(selectedCarData);
  const popupRef = useRef(null);

  // جلب السيارات المحفوظة من localStorage
  useEffect(() => {
    setSelectedCar(selectedCarData);
    console.log("selectedCarData>>>:", selectedCarData);
    if (isOpen) {
      setLoading(true);
      // محاكاة جلب البيانات من الكاش
      setTimeout(() => {
        const carsFromCache = localStorage.getItem("user_local_cars");
        console.log("carsFromCache>>>:", JSON.parse(carsFromCache));
        if (carsFromCache) {
          setSavedCars(JSON.parse(carsFromCache));
        } else {
        }
        setLoading(false);
      }, 500);
    }
  }, [isOpen, selectedCarData]);

  // إغلاق النافذة عند النقر خارجها
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // حذف سيارة من القائمة
  const handleDeleteCar = (carId) => {
    const updatedCars = savedCars.filter((car) => car.id !== carId);
    setSavedCars(updatedCars);
    localStorage.setItem("user_local_cars", JSON.stringify(updatedCars));
  };

  // اختيار سيارة
  const handleSelectCar = (car) => {
    onSelectModel(car);
    onClose();
  };

  // إضافة سيارة جديدة
  const handleAddNewCar = () => {
    onAddNewCar();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* خلفية معتمة */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backdropFilter: themeConfig.components.popup.overlayBlur,
          backgroundColor: themeConfig.components.popup.overlayBg,
          WebkitBackdropFilter: themeConfig.components.popup.overlayBlur,
          zIndex: 50,
        }}></div>

      {/* النافذة المنبثقة */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          ref={popupRef}
          className={`${themeConfig.components.popup.bgColor} ${themeConfig.components.popup.borderRadius} ${themeConfig.components.popup.shadow} w-full max-w-6xl mx-auto overflow-hidden`}
          style={{ maxHeight: "90vh", minHeight: "50vh", overflowY: "auto" }}>
          {/* الهيدر */}
          <div className={`bg-gradient-to-r ${themeConfig.colorClasses.bg.primary[500]} ${themeConfig.colorClasses.bg.primary[600]} p-6 text-white`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`${themeConfig.colorClasses.bg.white} p-2 ${themeConfig.sizes.borderRadius.base} ml-4`}>
                  <span className={`${themeConfig.typography.icons.medium} ${themeConfig.colorClasses.text.primary[600]}`}>{carSvg()}</span>
                </div>
                <div>
                  <h2 className={`text-white ${themeConfig.typography.headings.h3}`}>تصنيف حسب موديل السيارة</h2>
                  <p className={`text-green-100 ${themeConfig.typography.body.small}`}>اختر من سياراتك المحفوظة أو أضف سيارة جديدة</p>
                </div>
              </div>
              <button onClick={onClose} className={`text-white hover:text-gray-200 ${themeConfig.typography.icons.medium} p-2`}>
                ✕
              </button>
            </div>
          </div>

          {/* محتوى النافذة */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]" style={{ maxHeight: "calc(90vh - 100px)" }}>
            {/* قسم السيارات المحفوظة */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`${themeConfig.typography.headings.h4} ${themeConfig.colorClasses.text.neutral[800]} flex items-center`}>
                  <span className="ml-2"></span>
                  السيارات المحفوظة
                </h3>
                <span className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.medium}`}>{savedCars.length} سيارة</span>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mb-4"></div>
                  <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.medium}`}>جاري تحميل السيارات المحفوظة...</p>
                </div>
              ) : savedCars.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                  {savedCars.map((car) => (
                    <div
                      key={car.id}
                      className={`border ${car.id == selectedCar?.id ? themeConfig.colorClasses.border.primary[500] : themeConfig.colorClasses.border.neutral[200]} 
                      ${themeConfig.colorClasses.border.neutral[200]} ${themeConfig.sizes.borderRadius.md} p-5 hover:${themeConfig.colorClasses.border.primary[400]} hover:${themeConfig.shadows.md} ${themeConfig.transitions.all
                        }`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex  mb-3">
                            <div className={` p-2 ${themeConfig.sizes.borderRadius.base} ml-3`}>
                              <span className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.icons.small}`}>{carSvg()}</span>
                            </div>
                            <div>
                              <h4 className={`font-bold ${themeConfig.typography.subheadings.sub1} ${themeConfig.colorClasses.text.neutral[800]}`}>{car.name}</h4>
                              {/* <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.small}`}>موديل {car.year}</p> */}
                            </div>
                          </div>

                          <div className="space-y-2 mb-4">
                            {car.vin && (
                              <div className={`flex items-center ${themeConfig.typography.body.medium}`}>
                                <span className={`${themeConfig.typography.subheadings.sub2} ${themeConfig.colorClasses.text.neutral[500]} ml-2`}>رقم الهيكل:</span>
                                <span
                                  className={`font-mono ${themeConfig.typography.subheadings.sub2} ${themeConfig.colorClasses.bg.neutral[100]} px-2 py-1 ${themeConfig.sizes.borderRadius.sm} ${themeConfig.colorClasses.text.neutral[700]}`}>
                                  {car.vin}
                                </span>
                              </div>
                            )}

                            {/* <div className={`flex items-center ${themeConfig.typography.body.medium}`}>
                              <span className={`${themeConfig.colorClasses.text.neutral[500]} ml-2`}>آخر استخدام:</span>
                              <span className={`${themeConfig.colorClasses.text.neutral[700]}`}>{car.lastUsed}</span>
                            </div> */}
                          </div>

                          <div className="flex gap-3">
                            <button
                              onClick={() => {
                                if (car.id == selectedCar?.id) handleSelectCar(null);
                                else handleSelectCar(car);
                              }}
                              className={`flex-1 ${car.id == selectedCar?.id ? themeConfig.colorClasses.bg.neutral[500] : themeConfig.colorClasses.bg.primary[500]} hover:${themeConfig.colorClasses.bg.success[700]
                                } text-white py-2 px-4 ${themeConfig.sizes.borderRadius.base} ${themeConfig.typography.buttons.small} ${themeConfig.transitions.all}`}>
                              {car.id == selectedCar?.id ? " الغاء اختيار السيارة" : "اختر هذه السيارة"}
                            </button>
                            <button
                              onClick={() => handleDeleteCar(car.id)}
                              className={`${themeConfig.colorClasses.text.error[600]} hover:${themeConfig.colorClasses.text.error[800]} hover:${themeConfig.colorClasses.bg.error[50]} py-2 px-4 ${themeConfig.sizes.borderRadius.base} ${themeConfig.typography.buttons.small} ${themeConfig.transitions.all}`}>
                              حذف
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`text-center py-12 border-2 border-dashed ${themeConfig.colorClasses.border.neutral[300]} ${themeConfig.sizes.borderRadius.md}`}>
                  <div className={`${themeConfig.typography.icons.xlarge} mb-4`}>{carSvg()}</div>
                  <h4 className={`${themeConfig.typography.headings.h4} font-semibold ${themeConfig.colorClasses.text.neutral[700]} mb-2`}>لا توجد سيارات محفوظة</h4>
                  <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.medium} mb-6`}>لم تقم بحفظ أي سيارات بعد. أضف سيارة جديدة للبدء.</p>
                </div>
              )}
            </div>

            {/* قسم إضافة سيارة جديدة */}
            <div className={`border-t ${themeConfig.colorClasses.border.neutral[200]} pt-8`}>
              <h3 className={`${themeConfig.typography.headings.h4} ${themeConfig.colorClasses.text.neutral[800]} mb-6 flex items-center`}>
                <span className="ml-2 text-white"></span>
                إضافة سيارة جديدة
              </h3>

              <div className={`bg-gradient-to-r   border ${themeConfig.colorClasses.border.success[200]} ${themeConfig.sizes.borderRadius.md} p-6`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div className="mb-6 md:mb-0 md:ml-6">
                    <h4 className={`${themeConfig.typography.headings.h5} ${themeConfig.colorClasses.text.neutral[800]} mb-2`}>أضف سيارتك لتخصيص البحث</h4>
                    <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.medium}`}>احفظ معلومات سيارتك لتتمكن من تصفية قطع الغيار حسب المواصفات </p>
                    <ul className="mt-4 space-y-2 ${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.medium}">
                      <li className="flex items-center">
                        <span className={`${themeConfig.colorClasses.text.success[500]} ml-2`}>✓</span>
                        <span>قطع غيار متوافقة مع سيارتك</span>
                      </li>
                      <li className="flex items-center">
                        <span className={`${themeConfig.colorClasses.text.success[500]} ml-2`}>✓</span>
                        <span>توصيات مخصصة لسيارتك</span>
                      </li>
                      <li className="flex items-center">
                        <span className={`${themeConfig.colorClasses.text.success[500]} ml-2`}>✓</span>
                        <span>سهولة الوصول للسيارات المحفوظة</span>
                      </li>
                    </ul>
                  </div>

                  <button
                    onClick={handleAddNewCar}
                    className={`bg-gradient-to-r ${themeConfig.colorClasses.bg.primary[500]} ${themeConfig.colorClasses.bg.primary[600]} hover:${themeConfig.colorClasses.bg.success[600]} hover:${themeConfig.colorClasses.bg.success[700]} text-white font-bold py-4 px-8 ${themeConfig.sizes.borderRadius.md} ${themeConfig.typography.buttons.medium} ${themeConfig.transitions.all} ${themeConfig.shadows.lg} hover:${themeConfig.shadows.xl} whitespace-nowrap`}>
                    <span className="ml-2 text-white"></span>
                    إضافة سيارة جديدة
                  </button>
                </div>
              </div>

              {/* طريقة بديلة للبحث */}
              <div className="mt-8">
                <div className={`${themeConfig.colorClasses.bg.neutral[50]} border ${themeConfig.colorClasses.border.neutral[200]} ${themeConfig.sizes.borderRadius.md} p-5`}>
                  <h4 className={`font-bold ${themeConfig.colorClasses.text.neutral[800]} mb-3 flex items-center`}>
                    <span className="ml-2">
                      <img width="15" height="10" className="inline filter-primary size-21" src="/assets/icons/search.svg" alt="search" />
                    </span>
                    طريقة بحث بديلة
                  </h4>
                  <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.small} mb-4`}>يمكنك البحث يدوياً عن طريق رقم القطعة أو الاسم بدون حفظ السيارة</p>
                  <button
                    onClick={onClose}
                    className={`${themeConfig.colorClasses.text.primary[600]} hover:${themeConfig.colorClasses.text.primary[800]} font-medium ${themeConfig.typography.links.primary}`}>
                    العودة للبحث العادي →
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* الفوتر */}
          <div className={`border-t ${themeConfig.colorClasses.border.neutral[200]} p-4 ${themeConfig.colorClasses.bg.neutral[50]}`}>
            <div className="flex justify-between items-center">
              <div className={`${themeConfig.typography.captions.caption1}`}>جميع المعلومات محفوظة محلياً على جهازك</div>
              <button
                onClick={onClose}
                className={`${themeConfig.colorClasses.text.neutral[600]} hover:${themeConfig.colorClasses.text.neutral[800]} font-medium ${themeConfig.typography.links.secondary}`}>
                إلغاء
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================
// مكون Combobox قابل لإعادة الاستخدام
// ============================================
const CustomCombobox = ({ label, value, onChange, options = [], placeholder = "اختر...", icon, disabled = false, required = false, loading = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const comboboxRef = useRef(null);

  const filteredOptions = options?.filter(
    (option) =>
      option.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      option.searchText?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      option.text?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const selectedOption = options?.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div className="mb-4 sm:mb-6" ref={comboboxRef}>
      <label className={`block ${themeConfig.colorClasses.text.neutral[700]} font-bold mb-2 ${themeConfig.typography.inputs.label}`}>
        {icon && <span className="ml-1">{icon}</span>}
        {label}
        {required && <span className={`${themeConfig.colorClasses.text.error[500]} mr-1`}>*</span>}
      </label>

      <div className="relative">
        {/* زر Combobox الرئيسي */}
        <button
          type="button"
          className={`w-full p-3 sm:p-4 border ${themeConfig.colorClasses.border.neutral[300]} ${themeConfig.sizes.borderRadius.md} text-right flex items-center justify-between ${themeConfig.transitions.all
            } ${disabled
              ? `${themeConfig.colorClasses.bg.neutral[100]} ${themeConfig.colorClasses.border.neutral[300]} ${themeConfig.colorClasses.text.neutral[400]} cursor-not-allowed`
              : isOpen
                ? `${themeConfig.colorClasses.border.primary[500]} ring-2 ring-green-200 bg-white`
                : `${themeConfig.colorClasses.border.neutral[300]} hover:${themeConfig.colorClasses.border.primary[400]} bg-white`
            }`}
          onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
          disabled={disabled || loading}>
          <div className="flex items-center">
            {loading ? (
              <div className={`flex items-center ${themeConfig.colorClasses.text.neutral[500]}`}>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500 ml-2"></div>
                <span className={`${themeConfig.typography.body.small}`}>جاري التحميل...</span>
              </div>
            ) : selectedOption ? (
              <div className="flex items-center">
                {selectedOption.icon && <span className="ml-2">{selectedOption.icon}</span>}
                <span className={`${themeConfig.colorClasses.text.neutral[800]} ${themeConfig.typography.body.medium}`}>{selectedOption.text || selectedOption.name}</span>
                {selectedOption.details && <span className={`${themeConfig.colorClasses.text.neutral[500]} ${themeConfig.typography.captions.caption2} mr-2`}>({selectedOption.details})</span>}
              </div>
            ) : (
              <span className={`${themeConfig.colorClasses.text.neutral[400]} ${themeConfig.typography.body.medium}`}>{placeholder}</span>
            )}
          </div>
          <svg
            className={`w-5 h-5 ${themeConfig.colorClasses.text.neutral[400]} ${themeConfig.transitions.transform} ${isOpen ? "transform rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* القائمة المنسدلة */}
        {isOpen && !disabled && !loading && (
          <div
            className={`absolute z-50 w-full mt-1 bg-white border ${themeConfig.colorClasses.border.neutral[300]} ${themeConfig.sizes.borderRadius.md} ${themeConfig.shadows.lg}  overflow-auto`}
            style={{ maxHeight: "90vh", overflowY: "auto" }}>
            {/* شريط البحث داخل Combobox */}
            <div className="sticky top-0 bg-white p-2 border-b ${themeConfig.colorClasses.border.neutral[200]}">
              <div className="relative">
                <input
                  type="text"
                  className={`w-full p-2 pr-10 border ${themeConfig.colorClasses.border.neutral[300]} ${themeConfig.sizes.borderRadius.base} focus:ring-2 ${themeConfig.components.searchInput.focusRingColor} focus:${themeConfig.components.searchInput.focusBorderColor} text-right ${themeConfig.typography.inputs.placeholder}`}
                  placeholder="ابحث..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">🔍</div>
              </div>
            </div>

            {/* قائمة الخيارات */}
            <div className="py-1">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`w-full text-right px-4 py-3 hover:bg-green-50 ${themeConfig.transitions.colors} flex items-center justify-between ${value === option.value ? `${themeConfig.colorClasses.bg.primary[100]} ${themeConfig.colorClasses.text.primary[700]}` : `${themeConfig.colorClasses.text.neutral[700]}`
                      }`}
                    onClick={() => handleSelect(option)}>
                    <div className="flex items-center">
                      {option.icon && <span className="ml-2">{option.icon}</span>}
                      <div className="text-right">
                        <div className={`font-medium ${themeConfig.typography.body.medium}`}>{option.text || option.name}</div>
                        {option.description && <div className={`${themeConfig.typography.captions.caption2} mt-1`}>{option.description}</div>}
                      </div>
                    </div>
                    {value === option.value && <span className={`${themeConfig.colorClasses.text.primary[600]} ${themeConfig.typography.icons.xsmall}`}>✓</span>}
                  </button>
                ))
              ) : (
                <div className={`px-4 py-3 text-center ${themeConfig.colorClasses.text.neutral[500]} ${themeConfig.typography.body.small}`}>
                  {searchTerm ? "لا توجد نتائج" : "لا توجد خيارات متاحة"}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// مكون إضافة سيارة جديدة
// ============================================
export const AddNewCarPopup = ({ isOpen, onClose, onSave }) => {
  const { t: trans, i18n } = useTranslation([]);
  let lang = i18n.language || "ar";
  const [activeTab, setActiveTab] = useState("existing"); // 'existing' أو 'specs'
  const [selectedExistingCar, setSelectedExistingCar] = useState(null);
  const [specifications, setSpecifications] = useState({
    brand: "",
    group: "",
    model: "",
    year: "",
    vin: "",
  });

  // حالة التحميل لمحاكاة جلب البيانات من API
  const [loadingExistingCars, setLoadingExistingCars] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingTrims, setLoadingTrims] = useState(false);

  // بيانات افتراضية (قاعدة بيانات)
  const [existingCars, setExistingCars] = useState([]);

  // بيانات افتراضية للاختيارات (للتجربة)
  const [modelOptions, setModelOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [trimOptions, setTrimOptions] = useState([]);
  const [filteredSettings, setFilteredSettings] = useState({
    brands: [],
    groups: [],
    models: [],
    productModels: [],
  });

  const [settings, setSettings] = useState({
    brands: [],
    groups: [],
    models: [],
    productModels: [],
  });

  const { data: cars = [], isLoading, isError } = useUserCars();
  const popupRef = useRef(null);

  // جلب سيارات العميل من  البيانات (محاكاة API)
  useEffect(() => {
    setSelectedExistingCar(null);
    if (isOpen && activeTab === "existing") {
      setLoadingExistingCars(true);
      // محاكاة جلب البيانات من API
      setTimeout(() => {
        // بيانات  من  البيانات

        const mockDatabaseCars = cars.map((c) => ({
          id: c.CarID,
          name: c.ModelDescriptionAr,
          brand: c.BrandDescriptionAr,
          brandID: c.brandID,
          model: c.ModelTypeID,
          year: c.Model,
          vin: c.CarID,
          group: c.GroupDescriptionAr,
          groupID: c.GroupID,
          icon: "",
          details: c.CarID,
          modelCode: c.ModelCode, //=modelCode
          catalogueCode: "", //!=catalogueCode
          groupCode: "", //=modelName
          productNo: c.ProductNo, //=modelCode
          modelNotes: "", //!=modelNotes
        }));
        setExistingCars(mockDatabaseCars);
        setLoadingExistingCars(false);
      }, 1000);
    } else {
      fetchSettings();
    }
  }, [isOpen, activeTab]);

  // جلب المجموعات حسب البراند المختار (محاكاة)
  useEffect(() => {
    if (specifications.brand) {
      setLoadingGroups(true);
      setGroupOptions([]);

      const fGroups = settings?.groups?.filter((a) => a.brandID == specifications.brand && a.Year == specifications.year);

      setFilteredSettings({ ...settings, groups: fGroups, models: [], productModels: [] });

      setGroupOptions(fGroups);
      setLoadingGroups(false);
      // إعادة تعيين القيم التابعة
      setSpecifications((prev) => ({
        ...prev,
        model: "",
        group: "",
      }));
    } else {
      setGroupOptions([]);
    }
  }, [specifications.brand, specifications.year]);

  // جلب الموديل حسب المجموعة المختار (محاكاة)
  useEffect(() => {
    if (specifications.group) {
      setLoadingModels(true);
      setModelOptions([]);

      let fModels = [];
      try {
        const currentgroupData = settings?.groups?.filter((a) => a.brandID == specifications.brand && a.productGroupID == specifications.group && a.Year == specifications.year);
        const ListTreeGroups = currentgroupData && currentgroupData?.length > 0 ? currentgroupData[0].ListTreeGroups : specifications.group;

        // Split the comma-separated string into an array
        const listVArray = ListTreeGroups.split(",");

        // Assuming settings.productModels is an array of objects with properties productGroupID and Model
        fModels = settings?.productModels?.filter((a) => listVArray.includes(a.productGroupID) && a.Model == specifications.year);
      } catch {
        fModels = settings?.productModels?.filter((a) => a.productGroupID == specifications.group && a.Model == specifications.year);
      }

      setFilteredSettings({ ...filteredSettings, productModels: fModels });
      setModelOptions(fModels);
      setLoadingModels(false);

      setSpecifications((prev) => ({ ...prev, model: "" }));
    } else {
      setModelOptions([]);
    }
  }, [specifications.group]);

  const fetchSettings = async () => {
    //console.log("fetchSettings:>>>>>>>>>>>>>>>>>>>:", settings);
    if (settings?.brands.length <= 0) {
      try {
        if ("caches" in window) {
          var curl = "Settings-" + lang;

          // Opening that particular cache
          const cacheStorage = await caches.open("HJWeb");

          // Fetching that particular cache data
          const cachedResponse = await cacheStorage.match(curl);
          var settingResponseCash = await cachedResponse?.json();

          const currentSettings = { ...settings };
          currentSettings.brands = settingResponseCash?.brands;
          currentSettings.groups = settingResponseCash?.groups;
          currentSettings.models = settingResponseCash?.models;
          currentSettings.productModels = settingResponseCash?.productModels;
          setSettings(currentSettings);

          //---------------------------------------------------------------
          let currentvModelCode = {};
          currentSettings?.productModels?.map((item) => {
            if (item.ModelCode) {
              currentvModelCode[item.productGroupID] = item.ModelCode;
            }
          });
          // setVModelCode(currentvModelCode);
        }

        const settingResponse = await axios.request({
          baseURL: "/",
          url: config.controllURL + lang + "/api/Maintenance/Settings",
          method: "get",
        });

        const currentSettings = { ...settings };
        currentSettings.brands = settingResponse.data?.brands;
        currentSettings.groups = settingResponse.data?.groups;
        currentSettings.models = settingResponse.data?.models;
        currentSettings.productModels = settingResponse.data?.productModels;

        setSettings(currentSettings);

        // const fGroups = currentSettings?.groups?.filter((a) => a.brandID == brand);
        // setGroupData(fGroups);
        //---------------------------------------------------------------

        let currentvModelCode = {};
        currentSettings?.productModels?.map((item) => {
          currentvModelCode[item.productGroupID] = item.ModelCode;
        });

        //////console.log("currentvModelCode>>>>>>>>>", currentvModelCode);
        // setVModelCode(currentvModelCode);

        const data = new Response(JSON.stringify(settingResponse.data));

        if ("caches" in window) {
          // Opening given cache and putting our data into it
          caches.open("HJWeb").then((cache) => {
            cache.put("Settings-" + lang, data);
          });
        }
      } catch (error) {
        //////console.log(error);
      }
    }
  };
  const [loadingFetchVehicleDetailsByVIN, setLoadingFetchVehicleDetailsByVIN] = useState(false);
  const fetchVehicleDetailsByVIN = async (vin) => {
    try {
      setLoadingFetchVehicleDetailsByVIN(true);
      const response = await callFuncData("GetVehicleDetailsByVIN", { VIN: vin });

      const vDetails = response[0];
      console.log("fetchVehicleDetailsByVIN:", vDetails);
      console.log("ProductNo:", vDetails.ProductNo);
      if (vDetails) {
        const newCar = {
          id: `local_${Date.now()}`,
          name: `${vDetails?.ProductTypeAr} ${vDetails.Model}`,
          brand: vDetails.BrandAr,
          model: vDetails.ProductTypeAr,
          year: vDetails.Model,
          vin: vDetails.SN,
          group: vDetails.GroupAr,
          icon: vDetails === "تويوتا" ? "{carSvg()}" : "🏎️",
          details: vDetails.SN ? `رقم الهيكل: ${vDetails.SN}` : "تم الإضافة حديثاً",
          productNo: vDetails.ProductNo,
          modelCode: vDetails.ModelCode, //=modelCode
          catalogueCode: "", //!=catalogueCode
          groupCode: "", //=modelName
          modelNotes: "", //!=modelNotes
        };
        setLoadingFetchVehicleDetailsByVIN(false);
        return newCar;
      } else {
        return null;
      }
    } catch (error) {
      setLoadingFetchVehicleDetailsByVIN(false);
    }
  };
  // إغلاق النافذة عند النقر خارجها
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const handleSpecChange = (field, value) => {
    setSpecifications((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectExistingCar = (carId) => {
    const car = existingCars.find((c) => c.id === carId);
    console.log("handleSelectExistingCar:", car);
    setSelectedExistingCar(car);
  };

  const handleCheckVIN = async () => {
    if (activeTab === "existing" && specifications.vin != null && specifications.vin != "") {
      setSelectedExistingCar(null);
      const VehicleDetails = await fetchVehicleDetailsByVIN(specifications.vin);
      if (VehicleDetails) {
        setSelectedExistingCar(VehicleDetails);
      }
    }
  };

  const handleSubmit = async () => {
    if (activeTab === "existing" && selectedExistingCar) {
      // حفظ السيارة المختارة من  سيارات العميل
      onSave(selectedExistingCar);
      onClose();
      // } else if (activeTab === "existing" && specifications.vin != null && specifications.vin != "") {
      //   const VehicleDetails = await fetchVehicleDetailsByVIN(specifications.vin);
      //   if (VehicleDetails) {
      //     setSelectedExistingCar(VehicleDetails);
      //   }
    } else if (activeTab === "specs" && specifications.brand && specifications.model && specifications.year) {
      // إنشاء سيارة جديدة من المواصفات
      const brandName = settings?.brands?.find((b) => b.brandID == specifications.brand)?.DescriptionAr || specifications.brand;
      const productModel = filteredSettings?.productModels.find((m) => m.productTypeID === specifications.model);
      const modelName = productModel?.description || specifications.model;
      const groupName = filteredSettings?.groups.find((g) => g.productGroupID === specifications.group)?.description || "";

      const newCar = {
        id: `local_${Date.now()}`,
        name: `${modelName} ${specifications.year}`,
        brand: brandName,
        brandID: specifications.brand,
        model: modelName,
        typeID: specifications.model,
        year: specifications.year,
        vin: specifications.vin,
        group: groupName,
        groupID: specifications.group,
        icon: brandName === "تويوتا" ? "{carSvg()}" : "🏎️",
        details: specifications.vin ? `رقم الهيكل: ${specifications.vin}` : "تم الإضافة حديثاً",
        productNo: productModel.ProductNo,
        modelCode: productModel.ModelCode, //=modelCode
        catalogueCode: "", //!=catalogueCode
        groupCode: "", //=modelName
        modelNotes: "", //!=modelNotes
      };

      onSave(newCar);
      onClose();
    }
  };

  const isFormValid = () => {
    if (activeTab === "existing") {
      return selectedExistingCar !== null;
    } else {
      return specifications.brand && specifications.model && specifications.year;
    }
  };
  const isCheckVINValid = () => {
    if (activeTab === "existing") {
      return specifications.vin != null && specifications.vin != "";
    } else {
      return false;
    }
  };
  if (!isOpen) return null;

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backdropFilter: themeConfig.components.popup.overlayBlur,
          backgroundColor: themeConfig.components.popup.overlayBg,
          WebkitBackdropFilter: themeConfig.components.popup.overlayBlur,
          zIndex: 50,
        }}></div>

      {/* النافذة المنبثقة */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
        <div
          ref={popupRef}
          className={`flex flex-col ${themeConfig.components.popup.bgColor} ${themeConfig.components.popup.borderRadius} ${themeConfig.components.popup.shadow} w-full max-w-6xl mx-auto overflow-hidden`}
          style={{ maxHeight: "90vh", minHeight: "70vh", overflowY: "auto" }}>
          {/* الهيدر */}
          <div className={`bg-gradient-to-r ${themeConfig.colorClasses.bg.primary[500]} ${themeConfig.colorClasses.bg.primary[600]} p-4 sm:p-6 text-white`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between">
              <div className="flex items-center mb-4 sm:mb-0">
                <div className={`bg-white p-2 ${themeConfig.sizes.borderRadius.base} ml-0 sm:ml-4`}>
                  <span className={`${themeConfig.typography.icons.large} ${themeConfig.colorClasses.text.primary[600]}`}>{carSvg()}</span>
                </div>
                <div className="mr-2 sm:mr-0">
                  <h2 className={`text-white ${themeConfig.typography.headings.h3}`}>إضافة سيارة جديدة</h2>
                  <p className={`text-green-100 ${themeConfig.typography.body.small}`}> ادخل رقم الهيكل أو حدد موديل السيارة</p>
                </div>
              </div>
              <button onClick={onClose} className={`self-end text-white hover:text-gray-200 ${themeConfig.typography.icons.large} p-1 sm:p-2`}>
                ✕
              </button>
            </div>
          </div>

          {/* التابات */}
          <div className={`border-b ${themeConfig.colorClasses.border.neutral[200]}`}>
            <div className="flex">
              <button
                className={`flex-1 py-3 sm:py-4 text-center font-medium ${themeConfig.transitions.all} ${activeTab === "existing"
                  ? `border-b-2 ${themeConfig.colorClasses.border.primary[600]} ${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.colorClasses.bg.neutral[50]}`
                  : `${themeConfig.colorClasses.text.neutral[600]} hover:${themeConfig.colorClasses.text.neutral[800]} hover:${themeConfig.colorClasses.bg.neutral[50]}`
                  }`}
                onClick={() => setActiveTab("existing")}>
                <span className="flex items-center justify-center">
                  <span className="ml-1 sm:ml-2"></span>
                  <span className={`${themeConfig.typography.body.medium}`}>مركباتي او رقم الهيكل</span>
                </span>
              </button>

              <button
                className={`flex-1 py-3 sm:py-4 text-center font-medium ${themeConfig.transitions.all} ${activeTab === "specs"
                  ? `border-b-2 ${themeConfig.colorClasses.border.primary[600]} ${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.colorClasses.bg.neutral[50]}`
                  : `${themeConfig.colorClasses.text.neutral[600]} hover:${themeConfig.colorClasses.text.neutral[800]} hover:${themeConfig.colorClasses.bg.neutral[50]}`
                  }`}
                onClick={() => setActiveTab("specs")}>
                <span className="flex items-center justify-center">
                  <span className="ml-1 sm:ml-2"></span>
                  <span className={`${themeConfig.typography.body.medium}`}>موديل السيارة</span>
                </span>
              </button>
            </div>
          </div>

          {/* محتوى النافذة */}
          <div className="p-3 sm:p-6 overflow-y-auto flex-1 max-h-[calc(90vh-180px)]" style={{ maxHeight: "calc(90vh - 140px)" }}>
            {/* تاب: من  البيانات */}
            {activeTab === "existing" && (
              <div>
                <h3 className={`${themeConfig.typography.headings.h4} font-bold ${themeConfig.colorClasses.text.neutral[800]} mb-4 sm:mb-6`}>اختر من سياراتك المسجلة في النظام</h3>

                <CustomCombobox
                  label="سياراتك "
                  value={selectedExistingCar?.id || ""}
                  onChange={handleSelectExistingCar}
                  options={existingCars?.map((item) => {
                    return { ...item, text: item?.name, value: item?.id };
                  })}
                  placeholder="اختر سيارة   "
                  icon=""
                  required
                  loading={loadingExistingCars}
                />

                <div className={`mt-6 ${themeConfig.colorClasses.bg.neutral[50]} border ${themeConfig.colorClasses.border.neutral[200]} ${themeConfig.sizes.borderRadius.md} p-4`}>
                  <div className="flex items-start">
                    <span className={`${themeConfig.colorClasses.text.primary[600]} ml-2`}>
                      <img src="/assets/icons/info.svg" class="mx-3 text-inline-block nice-max-icon" alt="info" />
                    </span>
                    <div>
                      <p className={`${themeConfig.colorClasses.text.neutral[800]} font-medium mb-1 ${themeConfig.typography.subheadings.sub3}`}>معلومات</p>
                      <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.small}`}>
                        هذه السيارات مسجلة ضمن مركباتك الشخصية. يمكنك اختيار أي سيارة لإضافتها إلى قائمة التصنيف .
                      </p>
                    </div>
                  </div>
                </div>

                <h3 className={`${themeConfig.typography.headings.h4} mt-10 font-bold ${themeConfig.colorClasses.text.neutral[800]} mb-4 sm:mb-6`}> أو ادخل رقم الهيكل</h3>

                {/* حقل رقم الهيكل (نصي) */}
                <div className="mb-4 sm:mb-6">
                  <label className={`block ${themeConfig.colorClasses.text.neutral[700]} font-bold mb-2 ${themeConfig.typography.inputs.label}`}>
                    <span className="ml-1"></span>
                    رقم الهيكل (VIN)
                  </label>
                  <input
                    type="text"
                    placeholder="أدخل رقم الهيكل المكون من 17 خانة"
                    className={`w-full p-3 sm:p-4 border ${themeConfig.colorClasses.border.neutral[300]} ${themeConfig.sizes.borderRadius.md} focus:ring-2 ${themeConfig.components.searchInput.focusRingColor} focus:${themeConfig.components.searchInput.focusBorderColor} text-right bg-white ${themeConfig.typography.inputs.placeholder}`}
                    value={specifications.vin}
                    onChange={(e) => handleSpecChange("vin", e.target.value)}
                    maxLength={17}
                  />
                  <div className="flex flex-col md:flex-row md:items-center justify-between mt-5">
                    <p className={`${themeConfig.colorClasses.text.neutral[500]} ${themeConfig.typography.inputs.helper} `}>مثال: JTDBU4EE7EJ123456</p>

                    <button
                      onClick={handleCheckVIN}
                      disabled={!isCheckVINValid() || loadingFetchVehicleDetailsByVIN}
                      className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 ${themeConfig.sizes.borderRadius.base} font-medium ${themeConfig.transitions.all} ${themeConfig.typography.buttons.small
                        } ${isCheckVINValid() && !loadingFetchVehicleDetailsByVIN
                          ? `${themeConfig.colorClasses.bg.error[600]} hover:${themeConfig.colorClasses.bg.error[700]} text-white ${themeConfig.shadows.md} hover:${themeConfig.shadows.lg}`
                          : `${themeConfig.colorClasses.bg.neutral[300]} ${themeConfig.colorClasses.text.neutral[500]} cursor-not-allowed`
                        }`}>
                      {!loadingFetchVehicleDetailsByVIN ? " تحقق " : "جاري التحقق..."}
                    </button>
                  </div>
                </div>

                {loadingFetchVehicleDetailsByVIN ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mb-4"></div>
                    <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.medium}`}>جاري التحقق من هيكل السيارات ...</p>
                  </div>
                ) : (
                  selectedExistingCar && (
                    <div className={`mt-6 p-4  border ${themeConfig.colorClasses.border.primary[200]} ${themeConfig.sizes.borderRadius.md}`}>
                      <h4 className={`font-bold ${themeConfig.colorClasses.text.primary[800]} mb-3 ${themeConfig.typography.subheadings.sub2}`}>تفاصيل السيارة المختارة:</h4>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <span className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.small} ml-2 w-24`}>البراند:</span>
                          <span className={`font-medium ${themeConfig.colorClasses.text.neutral[800]} ${themeConfig.typography.body.medium}`}>{selectedExistingCar.brand}</span>
                        </div>

                        <div className="flex items-center">
                          <span className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.small} ml-2 w-24`}>رقم الهيكل:</span>
                          <span className={`font-mono font-medium ${themeConfig.colorClasses.text.neutral[800]} ${themeConfig.typography.body.small}`}>{selectedExistingCar.vin}</span>
                        </div>
                        {selectedExistingCar.name && (
                          <div className="flex items-center">
                            <span className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.small} ml-2 w-24`}>الفئة:</span>
                            <span className={`font-medium ${themeConfig.colorClasses.text.neutral[800]} ${themeConfig.typography.body.medium}`}>{selectedExistingCar.name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                )}

                <div className={`mt-6 ${themeConfig.colorClasses.bg.neutral[50]} border ${themeConfig.colorClasses.border.neutral[200]} ${themeConfig.sizes.borderRadius.md} p-4`}>
                  <div className="flex items-start">
                    <span className={`${themeConfig.colorClasses.text.primary[600]} ml-2`}>
                      {" "}
                      <img src="/assets/icons/info.svg" class="mx-3 text-inline-block nice-max-icon" alt="info" />
                    </span>
                    <div>
                      <p className={`${themeConfig.colorClasses.text.neutral[800]} font-medium mb-1 ${themeConfig.typography.subheadings.sub3}`}>معلومات</p>
                      <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.small}`}>
                        رقم الهيكل هو الرقم الذي يحدد نوع سيارتك و يتكون من 17 رقم يمكنك ايجاده في استمارة السيارة الخاصة بك، كما يمكنك إيجاده في الجزء السفلي اتجاه اليسار من لوحة القيادة أمام عجلة
                        القيادة.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* تاب: تحديد مواصفات جديدة */}
            {activeTab === "specs" && (
              <div>
                <h3 className={`${themeConfig.typography.headings.h4} font-bold ${themeConfig.colorClasses.text.neutral[800]} mb-4 sm:mb-6`}>حدد مواصفات السيارة </h3>

                {/* توجيهات */}
                <div className={`mb-6 border ${themeConfig.colorClasses.border.primary[200]} ${themeConfig.sizes.borderRadius.md} p-3 sm:p-4`}>
                  <div className="flex items-start">
                    <span className={`${themeConfig.colorClasses.text.primary[600]} ml-2`}>
                      {" "}
                      <img src="/assets/icons/info.svg" class="mx-3 text-inline-block nice-max-icon" alt="info" />
                    </span>
                    <div>
                      <p className={`${themeConfig.colorClasses.text.primary[800]} font-medium mb-1 ${themeConfig.typography.body.small}`}>كيفية الاستخدام</p>
                      <p className={`${themeConfig.colorClasses.text.primary[700]} ${themeConfig.typography.captions.caption2}`}>
                        اختر المواصفات بالترتيب باستخدام القوائم المنسدلة. الحقول المطلوبة: البراند، الموديل، السنة.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Combobox للبراند */}
                <CustomCombobox
                  label="البراند"
                  value={specifications.brand}
                  onChange={(value) => handleSpecChange("brand", value)}
                  // options={brandOptions}
                  options={settings?.brands?.map((item) => {
                    return { ...item, text: item?.description, value: item?.brandID };
                  })}
                  placeholder="اختر براند السيارة"
                  icon=""
                  required
                />
                {/* Combobox للسنة */}
                <CustomCombobox
                  label="سنة الموديل"
                  value={specifications.year}
                  onChange={(value) => handleSpecChange("year", value)}
                  // options={yearOptions}
                  options={settings?.models
                    ?.filter((item) => item?.modelID?.startsWith("20"))
                    .map((item) => ({
                      text: item?.modelID,
                      value: item?.modelID,
                      ...item,
                    }))}
                  placeholder="اختر سنة التصنيع"
                  icon=""
                  required
                />

                {/* Combobox للمجموعة */}
                <CustomCombobox
                  label="المجموعة"
                  value={specifications.group}
                  onChange={(value) => handleSpecChange("group", value)}
                  options={filteredSettings?.groups?.map((item) => {
                    return { text: item?.description, value: item?.productGroupID, ...item };
                  })}
                  placeholder={specifications.brand ? "اختر المجموعة" : "اختر براند أولاً"}
                  icon=""
                  disabled={!specifications.brand}
                  loading={loadingGroups}
                />
                {/* Combobox للموديل */}
                <CustomCombobox
                  label="الفئة"
                  value={specifications.model}
                  onChange={(value) => handleSpecChange("model", value)}
                  options={filteredSettings?.productModels?.map((item) => {
                    return { text: item?.ShortDescriptionEn, value: item?.productTypeID, ...item };
                  })}
                  placeholder={specifications.group ? "اختر الفئة" : "اختر المجموعة أولاً"}
                  icon="{carSvg()}"
                  required
                  disabled={!specifications.group}
                  loading={loadingModels}
                />
                {/* Combobox للفئة */}
                {/* <CustomCombobox
                  label="الفئة (Trim)"
                  value={specifications.trim}
                  onChange={(value) => handleSpecChange("trim", value)}
                  options={trimOptions}
                  placeholder={specifications.group ? "اختر الفئة" : "اختر المجموعة أولاً"}
                  icon=""
                  disabled={!specifications.group}
                  loading={loadingTrims}
                /> */}
                {/* عرض الملخص */}

                {(specifications.brand || specifications.group || specifications.year) && (
                  <div className={`mb-6 ${themeConfig.colorClasses.bg.neutral[50]} border ${themeConfig.colorClasses.border.neutral[200]} ${themeConfig.sizes.borderRadius.md} p-3 sm:p-4`}>
                    <h4 className={`font-bold ${themeConfig.colorClasses.text.neutral[800]} mb-2 ${themeConfig.typography.subheadings.sub2}`}>ملخص المواصفات المختارة:</h4>
                    <div className="space-y-2">
                      {specifications.brand && (
                        <div className={`flex items-center ${themeConfig.typography.body.medium}`}>
                          <span className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.small} ml-2 w-24`}>البراند:</span>
                          <span className={`font-medium ${themeConfig.colorClasses.text.neutral[800]} ${themeConfig.typography.body.medium}`}>
                            {settings?.brands?.find((b) => b.brandID == specifications.brand)?.DescriptionAr}
                          </span>
                        </div>
                      )}
                      {specifications.group && (
                        <div className={`flex items-center ${themeConfig.typography.body.medium}`}>
                          <span className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.small} ml-2 w-24`}>المجموعة:</span>
                          <span className={`font-medium ${themeConfig.colorClasses.text.neutral[800]} ${themeConfig.typography.body.medium}`}>
                            {filteredSettings?.groups?.find((g) => g.productGroupID == specifications.group)?.DescriptionAr}
                          </span>
                        </div>
                      )}
                      {specifications.model && (
                        <div className={`flex items-center ${themeConfig.typography.body.medium}`}>
                          <span className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.small} ml-2 w-24`}>الفئة:</span>
                          <span className={`font-medium ${themeConfig.colorClasses.text.neutral[800]} ${themeConfig.typography.body.medium}`}>
                            {filteredSettings?.productModels?.find((m) => m.productTypeID == specifications.model)?.ShortDescriptionEn}
                          </span>
                        </div>
                      )}

                      {specifications.year && (
                        <div className={`flex items-center ${themeConfig.typography.body.medium}`}>
                          <span className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.small} ml-2 w-24`}>السنة:</span>
                          <span className={`font-medium ${themeConfig.colorClasses.text.neutral[800]} ${themeConfig.typography.body.medium}`}>{specifications.year}</span>
                        </div>
                      )}
                      {specifications.vin && (
                        <div className={`flex items-center ${themeConfig.typography.body.medium}`}>
                          <span className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.small} ml-2 w-24`}>رقم الهيكل:</span>
                          <span className={`font-mono font-medium ${themeConfig.colorClasses.text.neutral[800]} ${themeConfig.typography.body.medium}`}>{specifications.vin}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className={`mt-6  border ${themeConfig.colorClasses.border.success[200]} ${themeConfig.sizes.borderRadius.md} p-4`}>
                  <div className="flex items-start">
                    <span className={`${themeConfig.colorClasses.text.success[600]} ml-2`}>
                      {" "}
                      <img src="/assets/icons/info.svg" class="mx-3 text-inline-block nice-max-icon" alt="info" />
                    </span>
                    <div>
                      <p className={`${themeConfig.colorClasses.text.neutral[800]} font-medium mb-1 ${themeConfig.typography.subheadings.sub3}`}>سيتم الحفظ في:</p>
                      <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.small}`}>
                        هذه السيارة ستُحفظ في ذاكرة التطبيق المحلية (LocalStorage) ويمكنك استخدامها لاحقاً في هذا الجهاز.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* الفوتر والأزرار */}
          <div className={`border-t ${themeConfig.colorClasses.border.neutral[200]} p-3 sm:p-4 ${themeConfig.colorClasses.bg.neutral[50]}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
              <div className={`${themeConfig.typography.captions.caption2}`}>{activeTab === "existing" ? " مركباتي" : "السيارات الجديدة تحفظ في ذاكرة التطبيق المحلية"}</div>

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 border ${themeConfig.colorClasses.border.neutral[300]} ${themeConfig.colorClasses.text.neutral[700]} hover:${themeConfig.colorClasses.bg.neutral[50]} ${themeConfig.sizes.borderRadius.base} ${themeConfig.transitions.all} ${themeConfig.typography.buttons.small}`}>
                  إلغاء
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={!isFormValid()}
                  className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 ${themeConfig.sizes.borderRadius.base} font-medium ${themeConfig.transitions.all} ${themeConfig.typography.buttons.small} ${isFormValid()
                    ? `${themeConfig.colorClasses.bg.primary[600]} hover:${themeConfig.colorClasses.bg.success[700]} text-white ${themeConfig.shadows.md} hover:${themeConfig.shadows.lg}`
                    : `${themeConfig.colorClasses.bg.neutral[300]} ${themeConfig.colorClasses.text.neutral[500]} cursor-not-allowed`
                    }`}>
                  {activeTab === "existing" ? "اختيار السيارة" : "حفظ السيارة "}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================
// المكون الرئيسي الذي يجمع كل شيء
// ============================================
const ToyotaPartsSearchPage = () => {
  const [showModelFilter, setShowModelFilter] = useState(false);
  const [showAddCar, setShowAddCar] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [searchResults, setSearchResults] = useState([]);

  const handleSearch = (query) => {
    console.log("بحث عن:", query);
    // هنا يمكنك إضافة منطق البحث الفعلي
    setSearchResults([{ id: 1, name: "نتيجة البحث", query }]);
  };

  const handleSelectModel = (car) => {
    setSelectedCar(car);
    console.log("تم اختيار السيارة:", car);
    // هنا يمكنك إضافة منطق التصفية حسب الموديل
  };

  const handleSaveNewCar = (newCar) => {
    console.log("تم حفظ السيارة الجديدة:", newCar);
    setSelectedCar(newCar);
  };
  const [showAddCarPopup, setShowAddCarPopup] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [localCars, setLocalCars] = useState([]);

  // تحميل السيارات المحفوظة محلياً
  useEffect(() => {
    const savedCars = localStorage.getItem("user_local_cars");
    if (savedCars) {
      setLocalCars(JSON.parse(savedCars));
    }
  }, []);

  const handleSaveCar = (car) => {
    console.log("تم حفظ السيارة:", car);
    setSelectedVehicle(car);

    if (car.id.startsWith("local_")) {
      // حفظ في localStorage للسيارات المحلية
      const existingCars = JSON.parse(localStorage.getItem("user_local_cars") || "[]");
      const carExists = existingCars.some((existingCar) => existingCar.id === car.id);

      if (!carExists) {
        const updatedCars = [...existingCars, car];
        localStorage.setItem("user_local_cars", JSON.stringify(updatedCars));
        setLocalCars(updatedCars);
      }
    } else {
      // سيارة من  البيانات - إضافتها إلى القائمة المحلية أيضاً
      const existingCars = JSON.parse(localStorage.getItem("user_local_cars") || "[]");
      const carExists = existingCars.some((existingCar) => existingCar.id === car.id);

      if (!carExists) {
        const updatedCars = [...existingCars, car];
        localStorage.setItem("user_local_cars", JSON.stringify(updatedCars));
        setLocalCars(updatedCars);
      }
    }
  };
  return (
    <div className="min-h-screen bg-gray-50">
      {/* رأس البحث */}
      <PartsSearchHeader onSearch={handleSearch} onOpenModelFilter={() => setShowModelFilter(true)} />

      {/* المحتوى الرئيسي */}
      <main className="container mx-auto px-4 py-8">
        {/* عرض السيارة المختارة */}
        <PartsSearchSelectedCar selectedCarData={selectedCar} onOpenModelFilter={(val) => setShowModelFilter(val)} onSelectedCar={(val) => setSelectedCar(val)} />

        {/* عرض نتائج البحث */}
        <div className={`${themeConfig.components.card.bgColor} ${themeConfig.sizes.borderRadius.md} ${themeConfig.components.card.shadow} overflow-hidden`}>
          <div className={`p-6 border-b ${themeConfig.colorClasses.border.neutral[200]}`}>
            <h2 className={`${themeConfig.typography.headings.h3} font-bold ${themeConfig.colorClasses.text.neutral[800]}`}>نتائج البحث عن قطع الغيار</h2>
            <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.medium}`}>{selectedCar ? `قطع غيار متوافقة مع ${selectedCar.model}` : "عرض جميع قطع الغيار"}</p>
          </div>

          {/* محتوى الصفحة (يمكن استبداله بمحتوى حقيقي) */}
          <div className="p-8 text-center">
            <div className={`${themeConfig.typography.icons.xlarge} mb-6`}>🔧</div>
            <h3 className={`${themeConfig.typography.headings.h3} font-bold ${themeConfig.colorClasses.text.neutral[700]} mb-4`}>
              {selectedCar ? `قطع غيار متوافقة مع ${selectedCar.model}` : "مرحباً في متجر قطع غيار تويوتا"}
            </h3>
            <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.medium} max-w-2xl mx-auto mb-8`}>
              استخدم شريط البحث في الأعلى للعثور على قطع الغيار التي تحتاجها، أو استخدم زر "تصنيف حسب موديل السيارة" لتصفية النتائج حسب سيارتك.
            </p>

            <button
              onClick={() => setShowModelFilter(true)}
              className={`bg-gradient-to-r ${themeConfig.colorClasses.bg.primary[600]} ${themeConfig.colorClasses.bg.primary[700]} hover:${themeConfig.colorClasses.bg.primary[700]} hover:${themeConfig.colorClasses.bg.primary[800]} text-white font-bold py-4 px-8 ${themeConfig.sizes.borderRadius.md} ${themeConfig.typography.buttons.large} ${themeConfig.transitions.all} ${themeConfig.shadows.lg} hover:${themeConfig.shadows.xl}`}>
              {carSvg()} البدء بتصنيف حسب الموديل
            </button>
          </div>
        </div>

        {/* معلومات إضافية */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className={`${themeConfig.components.card.bgColor} ${themeConfig.sizes.borderRadius.md} ${themeConfig.shadows.sm} p-6 border ${themeConfig.colorClasses.border.neutral[200]}`}>
            <div className={`${themeConfig.typography.icons.large} mb-4`}>⚡</div>
            <h4 className={`font-bold ${themeConfig.colorClasses.text.neutral[800]} mb-2 ${themeConfig.typography.subheadings.sub2}`}>بحث سريع</h4>
            <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.medium}`}>ابحث برقم القطعة أو اسمها للحصول على نتائج فورية</p>
          </div>

          <div className={`${themeConfig.components.card.bgColor} ${themeConfig.sizes.borderRadius.md} ${themeConfig.shadows.sm} p-6 border ${themeConfig.colorClasses.border.neutral[200]}`}>
            <div className={`${themeConfig.typography.icons.large} mb-4`}>{carSvg()}</div>
            <h4 className={`font-bold ${themeConfig.colorClasses.text.neutral[800]} mb-2 ${themeConfig.typography.subheadings.sub2}`}>تخصيص حسب الموديل</h4>
            <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.medium}`}>احفظ سيارتك لرؤية قطع الغيار المتوافقة معها فقط</p>
          </div>

          <div className={`${themeConfig.components.card.bgColor} ${themeConfig.sizes.borderRadius.md} ${themeConfig.shadows.sm} p-6 border ${themeConfig.colorClasses.border.neutral[200]}`}>
            <div className={`${themeConfig.typography.icons.large} mb-4`}>📦</div>
            <h4 className={`font-bold ${themeConfig.colorClasses.text.neutral[800]} mb-2 ${themeConfig.typography.subheadings.sub2}`}>توصيل سريع</h4>
            <p className={`${themeConfig.colorClasses.text.neutral[600]} ${themeConfig.typography.body.medium}`}>توصيل لجميع مناطق المملكة خلال 2-5 أيام عمل</p>
          </div>
        </div>
      </main>

      {/* نافذة تصنيف الموديل */}
      <ModelFilterPopup
        isOpen={showModelFilter}
        onClose={() => setShowModelFilter(false)}
        onSelectModel={handleSelectModel}
        onAddNewCar={() => {
          setShowModelFilter(false);
          setTimeout(() => setShowAddCar(true), 300);
        }}
      />

      {/* نافذة إضافة سيارة جديدة */}
      <AddNewCarPopup isOpen={showAddCar} onClose={() => setShowAddCar(false)} onSave={handleSaveCar} />

      {/* الفوتر */}
      <footer className={`mt-12 ${themeConfig.colorClasses.bg.neutral[800]} text-white py-8`}>
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className={`mb-2 ${themeConfig.typography.body.medium}`}>© 2023 أجزاء تويوتا السعودية. جميع الحقوق محفوظة.</p>
            <p className={`${themeConfig.colorClasses.text.neutral[400]} ${themeConfig.typography.body.small}`}>هذا النموذج لأغراض العرض التوضيحي</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ToyotaPartsSearchPage;
